/**
 * Aviso de sincronização pendente — o MOTOR: estado, notificação e "Sincronizar agora".
 * A regra (níveis, quando mostrar) está em utils/avisoSync; as telas em
 * components/avisoSync/AvisoSync.tsx.
 *
 * WEB: o navegador não agenda notificação para mais tarde (o app nativo agenda a de 30 min
 * e ela dispara com o app fechado). Aqui:
 *  - com a aba aberta, o nível 1 é o PUSH da tela (components/push) — `exibir: 1`;
 *  - com a aba em SEGUNDO PLANO e permissão de notificação, uma notificação do sistema
 *    (pelo service worker) por nível; clicar nela volta para a aba e sincroniza.
 * Quando tudo sobe, as notificações do sistema que ficaram abertas são retiradas.
 */
import {
  decidirAvisoSync,
  marcarVistoAvisoSync,
  type ContagemPendencias,
  type NivelAvisoSync,
  type VistosAvisoSync,
} from "@/utils/avisoSync";
import AsyncStorage from "@/platform/async-storage";
import { SyncService } from "./Sync";
import { T } from "@/utils/i18n";

const CHAVE_ESTADO = "@tirvumais:aviso_sync";
/** `data.tipo` da notificação — o RootLayout reconhece o clique por ele. */
export const TIPO_NOTIFICACAO_AVISO_SYNC = "aviso_sync";
/** WEB: `tag` das notificações do sistema (uma substitui a outra; retiradas juntas). */
const TAG_SISTEMA = "aviso-sync";

type EstadoPersistido = {
  /** Empresa + colaborador (outro login não herda os "vistos"). */
  dono: string;
  vistos: VistosAvisoSync;
  /** WEB: último nível avisado por notificação do sistema (aba em segundo plano). */
  sistemaNivel?: number;
};

export type SituacaoAvisoSync = {
  total: number;
  contagem: ContagemPendencias;
  nivel: NivelAvisoSync;
  idadeMs: number;
  /** O que a TELA deve mostrar agora: 1 push, 2 diálogo, 3 tela cheia, 0 nada. */
  exibir: NivelAvisoSync;
  sincronizando: boolean;
};

export type ResultadoSincronizacao = {
  restantes: number;
  semInternet: boolean;
};

const CONTAGEM_VAZIA: ContagemPendencias = {
  pontos: 0,
  assinaturas: 0,
  rondas: 0,
  livros: 0,
  outras: 0,
};

let situacao: SituacaoAvisoSync = {
  total: 0,
  contagem: CONTAGEM_VAZIA,
  nivel: 0,
  idadeMs: 0,
  exibir: 0,
  sincronizando: false,
};
const ouvintes = new Set<(s: SituacaoAvisoSync) => void>();
let donoAtual = "";

function publicar(parcial: Partial<SituacaoAvisoSync>) {
  situacao = { ...situacao, ...parcial };
  for (const fn of [...ouvintes]) {
    try {
      fn(situacao);
    } catch {
      /* ouvinte com defeito não impede os outros */
    }
  }
}

/** Avisa a cada mudança (e já entrega a situação atual). Devolve quem cancela. */
export function observarAvisoSync(fn: (s: SituacaoAvisoSync) => void): () => void {
  ouvintes.add(fn);
  fn(situacao);
  return () => {
    ouvintes.delete(fn);
  };
}

async function lerEstado(): Promise<EstadoPersistido | null> {
  try {
    const raw = await AsyncStorage.getItem(CHAVE_ESTADO);
    const v = raw ? JSON.parse(raw) : null;
    return v && typeof v === "object" && typeof v.dono === "string" ? v : null;
  } catch {
    return null;
  }
}

async function gravarEstado(e: EstadoPersistido | null): Promise<void> {
  try {
    if (e) await AsyncStorage.setItem(CHAVE_ESTADO, JSON.stringify(e));
    else await AsyncStorage.removeItem(CHAVE_ESTADO);
  } catch {
    /* sem estado gravado o pior caso é repetir um aviso */
  }
}

// ── notificação do sistema (aba em segundo plano) ───────────────────────────

const abaOculta = () => typeof document !== "undefined" && document.visibilityState === "hidden";

async function notificarSistema(nivel: NivelAvisoSync, total: number): Promise<boolean> {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return false;
  const titulo =
    nivel >= 3
      ? T("Registros há mais de 8 horas sem sincronizar")
      : nivel === 2
        ? T("Registros há mais de 1 hora sem sincronizar")
        : T("Itens aguardando sincronização");
  const body = T("{total} {v} neste aparelho. Toque para voltar ao Tirvu+ e sincronizar.", {
    total,
    v: total === 1 ? T("registro guardado") : T("registros guardados"),
  });
  const opcoes = {
    body,
    tag: TAG_SISTEMA,
    renotify: true,
    data: { tipo: TIPO_NOTIFICACAO_AVISO_SYNC },
    icon: "/pwa/icon-192.png",
    badge: "/pwa/icon-192.png",
  } as NotificationOptions;
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) await reg.showNotification(titulo, opcoes);
    else new Notification(titulo, opcoes);
    return true;
  } catch {
    return false;
  }
}

async function retirarNotificacoesSistema(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    const abertas = (await reg?.getNotifications({ tag: TAG_SISTEMA })) ?? [];
    abertas.forEach((n) => n.close());
  } catch {
    /* melhor esforço */
  }
}

// ── avaliação ────────────────────────────────────────────────────────────────

let filaAvaliacao: Promise<unknown> = Promise.resolve();

/**
 * Relê as pendências, decide o nível, cuida da notificação do sistema e publica a
 * situação. Chamadas são enfileiradas (nunca duas ao mesmo tempo).
 */
export function avaliarAvisoSync(dono: string): Promise<SituacaoAvisoSync> {
  const p = filaAvaliacao.then(() => avaliarAgora(dono));
  filaAvaliacao = p.catch(() => {});
  return p;
}

async function avaliarAgora(dono: string): Promise<SituacaoAvisoSync> {
  donoAtual = dono;
  const c = await SyncService.contarPendenciasSync();
  const agora = Date.now();
  let e = await lerEstado();
  if (!e || e.dono !== dono) e = { dono, vistos: {} };
  const d = decidirAvisoSync(
    e.vistos,
    { total: c.total, maisAntigaEm: c.maisAntigaEm },
    agora,
  );
  let vistos = d.vistos;
  let exibir = d.exibir;

  if (c.total === 0 && !c.indeterminado) {
    // Tudo enviado: some a notificação que tiver ficado aberta.
    if (e.sistemaNivel) await retirarNotificacoesSistema();
    e = { dono, vistos: {} };
  } else if (d.nivel === 0) {
    e = { ...e, sistemaNivel: undefined };
  } else if (exibir > 0 && abaOculta() && e.sistemaNivel !== exibir) {
    // Aba em segundo plano: avisa pelo sistema. O diálogo/tela cheia continuam esperando a
    // volta; o nível 1 não (o push já estaria velho quando a pessoa voltasse).
    if (await notificarSistema(exibir, c.total)) {
      e = { ...e, sistemaNivel: exibir };
      if (exibir === 1) {
        vistos = marcarVistoAvisoSync(vistos, 1, agora);
        exibir = 0;
      }
    }
  }

  await gravarEstado({ ...e, vistos });
  publicar({
    total: c.total,
    contagem: {
      pontos: c.pontos,
      assinaturas: c.assinaturas,
      rondas: c.rondas,
      livros: c.livros,
      outras: c.outras,
    },
    nivel: d.nivel,
    idadeMs: d.idadeMs,
    exibir,
  });
  return situacao;
}

/** A tela mostrou o aviso do nível (não repete enquanto o atraso continuar nele). */
export async function marcarAvisoSyncVisto(nivel: NivelAvisoSync): Promise<void> {
  if (nivel <= 0) return;
  const e = await lerEstado();
  if (e && e.dono === donoAtual) {
    await gravarEstado({ ...e, vistos: marcarVistoAvisoSync(e.vistos, nivel, Date.now()) });
  }
  if (situacao.exibir === nivel) publicar({ exibir: 0 });
}

let sincronizacaoEmCurso: Promise<ResultadoSincronizacao> | null = null;

/**
 * "Sincronizar agora": sincronização manual completa (ignora o intervalo de espera das
 * ações recusadas) e reavaliação. Toque repetido aproveita a que está em curso.
 */
export function sincronizarAgora(): Promise<ResultadoSincronizacao> {
  if (sincronizacaoEmCurso) return sincronizacaoEmCurso;
  publicar({ sincronizando: true });
  sincronizacaoEmCurso = (async () => {
    try {
      await SyncService.syncAll({ manual: true });
    } catch (e) {
      console.warn("[AvisoSync] sincronizar agora falhou:", e);
    }
    const s = donoAtual ? await avaliarAvisoSync(donoAtual) : null;
    const restantes = s ? s.total : (await SyncService.contarPendenciasSync()).total;
    const semInternet =
      restantes > 0 ? !(await SyncService.checkInternet().catch(() => false)) : false;
    return { restantes, semInternet };
  })().finally(() => {
    sincronizacaoEmCurso = null;
    publicar({ sincronizando: false });
  });
  return sincronizacaoEmCurso;
}

/** Depois de um envio sem a tela do aviso: se não sobrou nada, retira as notificações. */
export async function limparAvisoSyncSeEnviado(): Promise<void> {
  try {
    const c = await SyncService.contarPendenciasSync();
    if (c.total === 0 && !c.indeterminado) await retirarNotificacoesSistema();
  } catch {
    /* melhor esforço */
  }
}

/** A notificação clicada é a do aviso de sincronização? */
export function ehNotificacaoAvisoSync(data: unknown): boolean {
  return (data as any)?.tipo === TIPO_NOTIFICACAO_AVISO_SYNC;
}

// Clique na notificação. Abrindo o app por ela, a sessão ainda está sendo restaurada: o
// pedido fica guardado e a tela do aviso o executa quando houver sessão.
let pedidoPendente = false;
const ouvintesPedido = new Set<() => void>();

export function pedirSincronizacaoPelaNotificacao(): void {
  pedidoPendente = true;
  for (const fn of [...ouvintesPedido]) {
    try {
      fn();
    } catch {
      /* ignora */
    }
  }
}

/** Avisa quando há pedido (e já avisa se houver um guardado). */
export function observarPedidoSincronizacao(fn: () => void): () => void {
  ouvintesPedido.add(fn);
  if (pedidoPendente) fn();
  return () => {
    ouvintesPedido.delete(fn);
  };
}

/** Pega o pedido guardado (true uma vez só). */
export function consumirPedidoSincronizacao(): boolean {
  const p = pedidoPendente;
  pedidoPendente = false;
  return p;
}
